import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { RelationshipType } from '@models/relationships/relationship-type.enum';
import { IndividualData } from '@models/individuals/individual-data';
import { Relationship } from '@models/relationships/relationship';
import { SelectOption } from '@models/ui-models/select-options';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { PaymentMethod, paymentMethodsNames } from './company-change-name.model';
import { CompanyChangeDeregisterComponent } from '../../asic-forms/company';
import { IPDFSection } from "@models/ui-models/pdf-section.interface";

export enum VoluntaryDeregistrationStepsEnum {
  FormDescription = 0,
  ApplicantDeclaration = 1,
  DetailsOfTheApplicant = 2,
  AuthorisedSignatories = 3,
}

export enum ApplicantType {
  Company = 0,
  Director = 1
}

export class CompanyChangeDeregister extends EntityChangeData {
  static override readonly $type = 'CompanyChangeDeregister';
  isApplicantDirector: boolean;
  applicantIfDirector?: Relationship;
  applicantIfNominee?: IndividualData;
  declarationNoAssetsLiabilities: boolean;
  paymentMethod: PaymentMethod;

  constructor(data: Partial<CompanyChangeDeregister> = {}) {
    super(data);

    this.isApplicantDirector = data.isApplicantDirector ?? false;
    this.applicantIfDirector = data.applicantIfDirector ? new Relationship(data.applicantIfDirector) : data.applicantIfDirector;
    this.applicantIfNominee = data.applicantIfNominee ? new IndividualData(data.applicantIfNominee) : data.applicantIfNominee;
    this.declarationNoAssetsLiabilities = data.declarationNoAssetsLiabilities ?? false;
    this.paymentMethod = data.paymentMethod ?? PaymentMethod.INV;
  }

  toDictionary(): { key: string; value: string }[] {
    const dict = new ChangeDictionaryHelper();

    voluntaryDeregistrationDeclarations.forEach((key) => dict.add(key, 'Yes'));

    dict
      .add('Applicant', this.isApplicantDirector ? 'Director' : 'Company')
      .addIfValueIsNotEmpty('Name of the Director', this.applicantIfDirector?.individualDataOverride?.fullName)
      .addIfValueIsNotEmpty('Name of the Nominee', this.applicantIfNominee?.fullName)
      .addIfValueIsNotEmpty('Address of the Nominee', this.applicantIfNominee?.address?.normalizedFullAddress)
      .add('Payment Method', paymentMethodsNames[this.paymentMethod]);

    return dict.dictionary;
  }

  fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    (modalRef.componentInstance as CompanyChangeDeregisterComponent).companyChangeData.documentId = document.documentId;
    return modalRef;
  }

  fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit: boolean): NgbModalRef {
    const instance = modalRef.componentInstance as CompanyChangeDeregisterComponent;

    instance.isEdit = isEdit;
    instance.companyChangeData = new CompanyChangeData(company);
    instance.formModel = structuredClone(this);

    // TODO: remove after individualId works fine
    const officerIndividualsIdDictionary: Record<string, string> = {};

    instance.directorsOptions = structuredClone(company.currentOfficers)
      .reduce((directorsOptions: SelectOption[], officer) => {
          // TODO: remove after individualId works fine
          if (!(officer.individualDataOverride!.fullName in officerIndividualsIdDictionary)) {
            officerIndividualsIdDictionary[officer.individualDataOverride!.fullName] = Math.random().toString(36).substring(7);
          }

          // TODO: remove after individualId works fine
          officer.key = officer.individualId ?? officerIndividualsIdDictionary[officer.individualDataOverride!.fullName];

          const officerOption: SelectOption = {
            label: officer.individualDataOverride!.fullName,
            value: officer.key,
          };

          if (!(officer.key in instance.officers)) {
            instance.officers[officer.key] = [];
          }

          if (officer.type === RelationshipType.Director && !officer.end) {
            directorsOptions?.push(officerOption);
          }

          instance.officers[officer.key]?.push(officer);

          return directorsOptions;
        },
        []
      )
      .sort((a, b) => a.label?.toLowerCase().localeCompare(b.label?.toLowerCase()));

    return modalRef;
  }

  override prepareToPdf(): IPDFSection[] {
    const sectionDict = new ChangeDictionaryHelper();

    sectionDict.addSection('Applicant Declaration');
    voluntaryDeregistrationDeclarations.forEach((key) => sectionDict.add(key, 'Yes'));

    sectionDict
      .addSection('Details of the Applicant')
      .add('Applicant', this.isApplicantDirector ? 'Director' : 'Company')
      .addIfValueIsNotEmpty('Name of the Director', this.applicantIfDirector?.individualDataOverride?.fullName ?? '')
      .addIfValueIsNotEmpty('Name of the Nominee', this.applicantIfNominee?.fullName ?? '')
      .addIfValueIsNotEmpty('Address of the Nominee', this.applicantIfNominee?.address?.normalizedFullAddress.trim() ?? '')
      .add('Payment Method', paymentMethodsNames[this.paymentMethod]);

    return sectionDict.sections;
  }
}

export const voluntaryDeregistrationDeclarations = [
  'All members of the company agree to the deregistration',
  'The company is not carrying on business',
  'The company\'s assets are worth less than $1000',
  'The company has paid all fees and penalties payable under this Act',
  'The company has no outstanding liabilities',
  'The company is not a party to any legal proceedings'
];
